import React, {useState, useEffect, Fragment, useContext, useRef, useCallback, useMemo } from 'react';
import { MemoizedNotificationThreadList, MemoizedNotificationThread } from './MemoizedComponents';
import Loader from '../UI/Loader/Loader';
import ErrorBoundary from '../UI/ErrorBoundary/ErrorBoundary';
import { removeMessage, deleteNotificationThread } from '../../services/notification.services';
import { useNotificationThreads, NotificationThreadData } from '../../hooks/useNotificationThreads';
import AuthContext from '../../../src/store/auth-context';
import style from './Notifications.module.css';
import fontSize from '../Style/FontSize.module.css';
import common from '../Style/common.module.css';
import font from '../Style/FontFamily.module.css';
import button from '../Style/ButtonStyle.module.css';
import tableStyle from '../Style/TableModel.module.css';
import {useNavigate} from "react-router-dom";
import Modal from 'react-modal';
import { toast } from 'react-toastify';

// Constants
const UNSAVED_CHANGES_MESSAGE = 'You will lose the data you are editing, are you sure you want to leave?';
const MAX_SEARCH_TEXT_LENGTH = 50;
const SEARCH_DEBOUNCE_DELAY = 1000;

const NotificationsContainer = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const composeNotificationRef = useRef<any>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Guard: INTERNALADMIN and OWNER users must have orgId (from impersonation) to access notifications
    useEffect(() => {
        const usertype = authCtx.userData?.usertype;
        const hasOrgId = authCtx.userData?.orgId && authCtx.userData?.orgId !== '';
        
        if ((usertype === 'INTERNALADMIN' || usertype === 'OWNER') && !hasOrgId) {
            toast.info('Please select an organisation from Settings to view notifications');
            navigate('/Organisation#organisations');
        }
    }, [authCtx.userData?.usertype, authCtx.userData?.orgId, navigate]);

    // Use custom hook for notification threads management
    const {
        threads,
        selectedThread,
        selectedThreadUUID,
        activatedNotification,
        selectUsersForGroup,
        setSelectUsersForGroup,
        isLoading,
        // error, // Available if needed for custom error handling
        currentPage,
        hasMore,
        loadThreads,
        loadMoreThreads,
        setActiveThread,
        createNotification,
        removeThread,
        resetSelectedThread,
        setThreads,
        setSelectedThread,
        setHasMore,
        setCurrentPage,
    } = useNotificationThreads({
        authToken: authCtx.token,
        onError: (error) => {
            console.error('Notification threads error:', error);
            toast.error(error.message || 'Failed to load notifications');
        },
    });


    // UI state
    const [modalIsOpen, setIsOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isNotificationCardOpen, setIsNotificationCardOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isContactGroupsDescendingOrder, setIsContactGroupsDescendingOrder] = useState(true);

    // Selection state
    const [selectedChannels, setSelectedChannel] = useState<string[]>([]);
    const [selectedChannelFilter, setSelectedChannelFilter] = useState<string[]>([]);

    // Search state
    const [notificationThreadSearchText, setNotificationThreadSearchText] = useState('');
    const [currentSelectedGroup] = useState<any>(null); // Kept for compatibility with NotificationThread component
    const [selectedGroupTitle, setSelectedGroupTitle] = useState<string>('');

    // Memoized computed values for performance
    const hasSelectedChannels = useMemo(() => selectedChannels.length > 0, [selectedChannels.length]);
    
    
    // First sort all notifications, then apply filtering if needed
    const sortedNotificationThreads = useMemo(() => {
        // First sort all notification threads
        const sorted = [...threads];
        if (isContactGroupsDescendingOrder) {
            sorted.sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
        } else {
            sorted.sort((a, b) => new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime());
        }
        
        // Then apply channel filtering if any filters are selected
        if (selectedChannelFilter.length === 0) {
            return sorted;
        }
        
        return sorted.filter(notification => {
            const channels = notification.channels ? notification.channels.split(',') : [];
            return selectedChannelFilter.some(filter => channels.includes(filter));
        });
    }, [threads, selectedChannelFilter, isContactGroupsDescendingOrder]);

    useEffect(() => {
       
        setHasMore(true);
        if (!authCtx.isLoggedIn) {
            navigate('/login');
            return;
        }

        // Load notification threads
        loadThreads(0, '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authCtx.isLoggedIn, navigate]);

    const loadMoreOnScroll = useCallback((pageNumber: number) => {
        setCurrentPage(pageNumber);
        loadMoreThreads(pageNumber, notificationThreadSearchText);
    }, [notificationThreadSearchText, loadMoreThreads, setCurrentPage]);


    const clearNotifications = useCallback(() => {
        setThreads([]);
    }, [setThreads]);

    const clearRecipientsSelection = useCallback(() => {
        // clearing the selected recipients
        if (composeNotificationRef.current) {
            composeNotificationRef.current.clearRecipientsSelection();
        }
    }, []);


    const applySearchText = useCallback((searchText: string) => {
        setHasMore(true);
        clearRecipientsSelection();
        
        // max length of search text is MAX_SEARCH_TEXT_LENGTH
        if (searchText.length > MAX_SEARCH_TEXT_LENGTH) {
            console.warn(`Max length of search text is ${MAX_SEARCH_TEXT_LENGTH}`);
            return;
        }
        
        clearNotifications();
        setNotificationThreadSearchText(searchText);
        
        if (searchText !== '' && searchText.length > 2) {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            const timeout = setTimeout(() => {
                setIsSearching(true);
                loadThreads(0, searchText);
            }, SEARCH_DEBOUNCE_DELAY);
            searchTimeoutRef.current = timeout;
        } else if (searchText === '') {
            clearNotifications();
            loadThreads(0, '');
        }
    }, [clearNotifications, clearRecipientsSelection, loadThreads, setHasMore]);

    // Cleanup searchTimeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    /** For notification selection **/
    const modalClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const leavePost = useCallback(() => {
        if (composeNotificationRef.current) {
            composeNotificationRef.current.clearEditor();
            setIsOpen(false);
        }
    }, []);

    const setActiveNotificationThread = useCallback(async (
        notificationThreadUuid: string, 
        notificationThreadStatus: string, 
        channels: string[], 
        title: string
    ) => {
        console.log('setActiveNotificationThread called with:', {
            notificationThreadUuid,
            notificationThreadStatus,
            channels,
            title
        });
        // Check if user is editing and warn them
        if (isEditing) {
            setIsOpen(true);
            return;
        }

        if (composeNotificationRef.current) {
            composeNotificationRef.current.clearEditor();
        }

        setSelectedChannel(channels);
        setIsNotificationCardOpen(true);
        setSelectedGroupTitle(title);

        try {
           
            await setActiveThread(notificationThreadUuid, notificationThreadStatus, channels, title);
            
            // Set previewed recipient data in compose notification
            if (composeNotificationRef.current && activatedNotification?.previewedRecipientData) {
                const previewedData = Array.isArray(activatedNotification.previewedRecipientData)
                    ? activatedNotification.previewedRecipientData
                    : [];
                
                composeNotificationRef.current.setPreviewedRecipientData(previewedData);
            }
        } catch (error) {
            console.error('Error activating notification thread:', error);
            toast.error('Failed to load notification thread');
        }
    }, [isEditing, setActiveThread, activatedNotification]);
    
    // Add the newly created notification to the list on the top
    const createNotificationHandler = useCallback((data: NotificationThreadData, isCreatingRecipient: boolean) => {
        createNotification(data, isCreatingRecipient);
        setIsNotificationCardOpen(true);
    }, [createNotification]);
    
    // For resetting the selected state
    const resetNotificationBox = useCallback(() => {
        resetSelectedThread();
        setIsNotificationCardOpen(false);
    }, [resetSelectedThread]);

    // For resetting selected channels
    const resetChannelsHandler = useCallback(() => {
        setSelectedChannel([]);
    }, []);
    
    // Remove notification
    const removeNotification = useCallback((id: string, shouldRemoveGroup: boolean) => {
        // Extract usersGroupId from message before deleting it
        const messageToDelete = selectedThread.find((msg) => msg.id === id);
        const threadUuidFromMessage = messageToDelete?.usersGroupId || selectedThreadUUID;
        
        const updatedNoti = selectedThread.filter((msg) => msg.id !== id);
        setSelectedThread(updatedNoti);
        
        removeMessage(id, authCtx.token).then((res) => {
            if (shouldRemoveGroup) {
                removeThread(threadUuidFromMessage!);
                deleteNotificationThread(threadUuidFromMessage!, authCtx.token);
                resetNotificationBox();
                setSelectedChannel([]);
            }
        }).catch((err) => {
            console.error('Error removing notification:', err);
            toast.error('Failed to remove notification');
        });
    }, [selectedThread, authCtx.token, selectedThreadUUID, resetNotificationBox, removeThread, setSelectedThread]);

    const updateNotificationAtUuid = useCallback((id: string, messageBody: string, rawMessageBody: string) => {
        const updatedMessages = selectedThread.map((msg) => 
            msg.id === id 
                ? { ...msg, messageBody, rawMessageBody, flag: '' } 
                : msg
        );
        setSelectedThread(updatedMessages);
    }, [selectedThread, setSelectedThread]);

    const updateNotificationFlagAtUuid = useCallback((id: string, flag: string) => {
        const updatedMessages = selectedThread.map((msg) => 
            msg.id === id 
                ? { ...msg, flag } 
                : msg
        );
        setSelectedThread(updatedMessages);
    }, [selectedThread, setSelectedThread]);

    // For updating message pinned status === true
    const addMessageToPin = useCallback((messageId: string) => {
        // Remove pin from all messages and add pin only to the selected message
        const updatePinnedMessage = selectedThread.map((msg) => 
            msg.id === messageId 
                ? { ...msg, isPinned: true } 
                : { ...msg, isPinned: false }
        );
        setSelectedThread(updatePinnedMessage);
    }, [selectedThread, setSelectedThread]);
    
    // Selected Channels for compose notification
    const composeChannelsHandler = useCallback((arrChannelIds: string[]) => {
        setSelectedChannel(arrChannelIds);
        resetNotificationBox();
    }, [resetNotificationBox]);

    // For sorting contact group message
    const sortingNotificationHandler = useCallback(() => {
        const messages = [...threads];

        if (isContactGroupsDescendingOrder) {
            messages.sort((a, b) => (a.groupIntId || 0) - (b.groupIntId || 0));
        } else {
            messages.sort((a, b) => (b.groupIntId || 0) - (a.groupIntId || 0));
        }
        
        setIsContactGroupsDescendingOrder(order => !order);
        setThreads(messages);
        
        // Use modern scrollIntoView instead of jQuery
        setTimeout(() => {
            const filterAction = document.querySelector('.filterAction');
            if (filterAction) {
                filterAction.scrollIntoView({ behavior: 'smooth' });
            }
        }, 0);
    }, [threads, isContactGroupsDescendingOrder, setThreads]);


    // Apply Channel filter Handler
    const applyChannelFilterHandler = useCallback((selectedFilterChannels: string[]) => {
        setSelectedChannelFilter(selectedFilterChannels);
    }, []);

    // For resetting checked channel filter
    const resetChannelFilterHandler = useCallback(() => {
        setSelectedChannelFilter([]);
    }, []);

    // Deactivate notification thread Handler
    const deactivateNotificationThreadHandler = useCallback((contactGroup: string) => {
        const messages = threads.map((msg) => 
            msg.usersGroupId === contactGroup 
                ? { ...msg, notificationThreadStatus: 'INACTIVE' } 
                : msg
        );
        setThreads(messages);
        
        // If the deactivated thread is currently selected, update its status in the active view
        if (selectedThreadUUID === contactGroup) {
            // Trigger a re-render by calling setActiveThread with the updated status
            const deactivatedThread = messages.find(msg => msg.usersGroupId === contactGroup);
            if (deactivatedThread) {
                const channels = deactivatedThread.channels ? deactivatedThread.channels.split(',') : [];
                setActiveThread(contactGroup, 'INACTIVE', channels, deactivatedThread.name || deactivatedThread.title);
            }
        }
    }, [threads, setThreads, selectedThreadUUID, setActiveThread]);
    
    // Handler for navigating to existing DM thread
    const navigateToDmThreadHandler = useCallback(async (groupUuid: string) => {
        try {
            // Check if thread already exists in current list
            const existingThread = threads.find(t => t.usersGroupId === groupUuid);
            
            if (existingThread) {
                // Thread exists, just activate it
                const channels = existingThread.channels ? existingThread.channels.split(',') : [];
                await setActiveNotificationThread(
                    existingThread.usersGroupId, 
                    existingThread.notificationThreadStatus, 
                    channels, 
                    existingThread.name
                );

                if (composeNotificationRef.current && existingThread.previewedRecipientData) {
                        try {
                            // Parse previewedRecipientData if it's a JSON string
                            let parsedData = existingThread.previewedRecipientData;
                            if (typeof parsedData === 'string') {
                                parsedData = JSON.parse(parsedData);
                            }
                            const previewedData = Array.isArray(parsedData) ? parsedData : [];
                            composeNotificationRef.current.setPreviewedRecipientData(previewedData);
                        } catch (error) {
                            console.error('Error parsing previewedRecipientData:', error);
                            composeNotificationRef.current.setPreviewedRecipientData([]);
                        }
                    }
                
            } else {
                // Thread doesn't exist in list, fetch it from backend
                const response = await import('../../services/group.service').then(module => 
                    module.getSelectUserGroupForDM(groupUuid, authCtx.token)
                );
                
                if (response) {
                    const group = response;
                    
                    // Create thread data matching NotificationThreadData structure
                    const newThread: NotificationThreadData = {
                        id: group.uuid,
                        usersGroupId: group.uuid,
                        title: group.name || 'Direct Message',
                        messageBody: group.last_notification_snippet || 'No messages yet',
                        rawMessageBody: group.last_notification_snippet || '',
                        notificationThreadStatus: group.status || 'ACTIVE',
                        channels: group.selectedChanel || '5',
                        groupIntId: group.id,
                        lastModified: group.modifiedDate || new Date().toISOString(),
                        emailOnlySubscribers: group.emailOnlySubscribers || [],
                        previewedRecipientData: group.previewedRecipientData || [],
                        authorName: group.authorName || '',
                        authorImageUrl: group.authorImageUrl || '',
                        schoolImage: group.schoolImage || '',
                        authorId: group.createdBy || '',
                        orgUuid: group.orguuid || '',
                        selectedUsers: group.selectedUsers ? [group.selectedUsers] : [],
                        selectedChanel: group.selectedChanel || '5',
                        createdBy: group.createdBy || '',
                        createDate: group.createDate || new Date().toISOString(),
                        isAllEmailOnlySubscribersSelected: group.isAllEmailOnlySubscribersSelected || 'false',
                        isAllSchoolLinkAppUsersSelected: group.isAllSchoolLinkAppUsersSelected || 'false',
                        usertype: group.usertype || 'OWNER',
                        threadName: group.name || 'Direct Message',
                        threadUuid: group.uuid
                    };
              
                    
                    // Add thread to top of list
                    setThreads([newThread, ...threads]);
                    
                    // Activate the thread
                    const channels = group.selectedChanel ? group.selectedChanel.split(',') : ['5'];
                    await setActiveNotificationThread(
                        group.uuid,
                        group.status || 'ACTIVE',
                        channels,
                        group.name || 'Direct Message'
                    );

                    if (composeNotificationRef.current && group.previewedRecipientData) {
                        try {
                            // Parse previewedRecipientData if it's a JSON string
                            let parsedData = group.previewedRecipientData;
                            if (typeof parsedData === 'string') {
                                parsedData = JSON.parse(parsedData);
                            }
                            const previewedData = Array.isArray(parsedData) ? parsedData : [];
                            composeNotificationRef.current.setPreviewedRecipientData(previewedData);
                        } catch (error) {
                            console.error('Error parsing previewedRecipientData:', error);
                            composeNotificationRef.current.setPreviewedRecipientData([]);
                        }
                    }
                    
                    toast.success('Navigated to existing conversation');
                } else {
                    toast.error('Failed to load conversation');
                }
            }
        } catch (error) {
            console.error('Error navigating to DM thread:', error);
            toast.error('Failed to open conversation');
        }
    }, [threads, setThreads, setActiveNotificationThread, authCtx.token]);
    
    return (
        <ErrorBoundary 
            onError={(error, errorInfo) => {
                console.error('ErrorBoundary caught error:', error, errorInfo);
                toast.error('An unexpected error occurred. Please refresh the page.');
            }}
        >
            <Fragment> 
                {isLoading && <Loader />}
                <div className={style.rightbar + ' ' + common.screenContainer}>
                    <div id="NotificationsThreadList" className={"col-xl-4 col-md-6 col-xs-6 " + common.leftSection}>
                        <div className={style.Messagebox}>
                            <MemoizedNotificationThreadList
                                notifications={sortedNotificationThreads}
                                loadMoreOnScroll={loadMoreOnScroll}
                                setCurrentPage={setCurrentPage}
                                currentPage={currentPage}
                                selectedContactGroup={selectedThreadUUID}
                                setActiveNotificationThread={setActiveNotificationThread}
                                selectedChannels={selectedChannels}
                                onComposeChannels={composeChannelsHandler}
                                resetChannelsHandler={resetChannelsHandler}
                                resetNotificationBox={resetNotificationBox}
                                sortingNotificationHandler={sortingNotificationHandler}
                                contactGroupsOrder={isContactGroupsDescendingOrder}
                                selectedChannelFilter={selectedChannelFilter}
                                applyChannelFilterHandler={applyChannelFilterHandler}
                                resetChannelFilterHandler={resetChannelFilterHandler}
                                deactivateNotificationHandler={deactivateNotificationThreadHandler}
                                clearRecipientsSelection={clearRecipientsSelection}
                                notificationThreadSearchText={notificationThreadSearchText}
                                applySearchText={applySearchText}
                                isSearching={isSearching}
                                hasMore={hasMore}
                            />
                        </div>
                    </div>

                    {hasSelectedChannels && <div className={"col-xl-5 col-md-7 col-xs-6 " + common.rightSection}>
                        <div id="NotificationThreadDetail" className={style.Sendmessagebox + ' ' + common.eritorcontainer}>
                            {(React.createElement(MemoizedNotificationThread as any, {
                                ref: composeNotificationRef,
                                selectedNtification: selectedThread,
                                notificationThreadStatus: activatedNotification?.notificationThreadStatus,
                                addNotificationsToList: createNotificationHandler,
                                loadNotificationThreads: loadThreads,
                                selectedNotificationThreadUuid: selectedThreadUUID,
                                setSelectedNotificationThreadUuid: setActiveThread,
                                isCardOpen: isNotificationCardOpen,
                                resetNotificationBox: resetNotificationBox,
                                onRemoveNotification: removeNotification,
                                onCreatePinMessage: addMessageToPin,
                                updateNotificationAtUuid: updateNotificationAtUuid,
                                channels: selectedChannels,
                                emailOnlySubscribers: activatedNotification?.emailOnlySubscribers || [],
                                currentSelectedGroup: currentSelectedGroup,
                                selectUsersForGroup: selectUsersForGroup,
                                setSelectUsersForGroup: setSelectUsersForGroup,
                                selectedGroupTitle: selectedGroupTitle,
                                setSelectedGroupTitle: setSelectedGroupTitle,
                                setIsEditing: setIsEditing,
                                updateNotificationFlagAtUuid: updateNotificationFlagAtUuid,
                                onSelectedNotification: setActiveNotificationThread,
                                onNavigateToDmThread: navigateToDmThreadHandler
                            } as any))}
                        </div>
                    </div>}
                </div>

                <Modal isOpen={modalIsOpen} className="alertBgContainer" contentLabel="Unsaved Changes Warning" >
                    <div className='alertBox'>
                        <h4 className={tableStyle.alertTitle}>Unsaved Changes</h4>
                        <p className={fontSize.smSize + ' ' + font.fontRegular + ' ' + common.pTag}>{UNSAVED_CHANGES_MESSAGE}</p>
                        <div className='bottomArea'>
                            <button onClick={modalClose} className={button.smBtn + ' ' + button.lightBrownButton}>Cancel</button>
                            <button onClick={leavePost} className={button.smBtn + ' ' + button.blueButton + ' ' + button.buttonSpaceLeft}>Leave Post</button>
                        </div>
                    </div>
                </Modal>
            </Fragment>
        </ErrorBoundary>
    );
}
export default NotificationsContainer;
